import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import Pagination from '../../components/Pagination/Pagination';
import { getBlogPosts, deleteBlogPost } from '../../services/BlogService';

function BlogPosts() {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    const [posts, setPosts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [notification, setNotification] = useState({});

    const PAGE_SIZE = 20;

    useEffect(() => {
        loadPosts(page);
    }, [page]);

    async function loadPosts(p) {
        try {
            const data = await getBlogPosts(p, PAGE_SIZE);
            setPosts(data.items || []);
            setTotal(data.totalCount || 0);
        } catch (err) {
            setNotification({ type: 'error', text: err.response?.data || err.message });
        }
    }

    async function onDeleteClick(id) {
        if (!window.confirm(t('blog.confirmDelete'))) return;
        try {
            await deleteBlogPost(id);
            loadPosts(page);
            setNotification({ type: 'success', text: t('blog.deletedSuccess') });
        } catch (err) {
            setNotification({ type: 'error', text: err.response?.data || err.message });
        }
    }

    function getStatusBadge(post) {
        if (post.isPublished) return <span className="badge bg-success py-1">Published</span>;
        return <span className="badge bg-warning py-1 text-dark">Draft</span>;
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">{t('blog.posts')}</h1>
                        <p className="mb-0 text-muted">{total} {t('blog.totalPosts')}</p>
                    </div>
                    <div className="btn-toolbar mb-md-0">
                        <Link to="/blog/new" className="btn btn-sm btn-gray-800 d-inline-flex align-items-center">
                            <svg className="icon icon-xs me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t('blog.newPost')}
                        </Link>
                    </div>
                </div>

                <div className="card card-body border-0 shadow mb-4">
                    <div className="table-responsive">
                        {/* gh-table-stack: 7 columns can never fit a phone/tablet, so
                            below 992px each row becomes a label/value card (labels come
                            from the data-label attributes below). */}
                        <table className="table table-hover align-middle gh-table-stack">
                            <thead>
                                <tr>
                                    <th>{t('blog.title')}</th>
                                    <th>{t('blog.category')}</th>
                                    <th>{t('blog.author')}</th>
                                    <th>{t('blog.status')}</th>
                                    <th>{t('blog.views')}</th>
                                    <th>{t('blog.publishedAt')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {posts.map(post => (
                                    <tr key={post.id}>
                                        <td data-label={t('blog.title')}>
                                            <div className="fw-semibold text-break">{post.title}</div>
                                            <small className="text-muted text-break-all">{post.slug}</small>
                                        </td>
                                        <td data-label={t('blog.category')}>
                                            {post.categoryName
                                                ? <span className="badge bg-secondary py-1">{post.categoryName}</span>
                                                : <span className="text-muted">—</span>
                                            }
                                        </td>
                                        <td data-label={t('blog.author')}>{post.authorName || '—'}</td>
                                        <td data-label={t('blog.status')}>{getStatusBadge(post)}</td>
                                        <td data-label={t('blog.views')}>{post.viewCount || 0}</td>
                                        <td data-label={t('blog.publishedAt')}>
                                            {post.publishedAt
                                                ? new Date(post.publishedAt).toLocaleDateString()
                                                : '—'
                                            }
                                        </td>
                                        <td data-label={t('common.actions')}>
                                            <div className="d-flex gap-2 flex-wrap justify-content-end">
                                                <Link to={`/blog/${post.id}`} className="btn btn-sm btn-outline-secondary">
                                                    {t('blog.edit')}
                                                </Link>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => onDeleteClick(post.id)}
                                                >
                                                    {t('blog.delete')}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {posts.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="text-center text-muted py-4">
                                            {t('blog.noPosts')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Pagination count={total} pageSize={PAGE_SIZE} page={page} onClick={setPage} />
                </div>
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default BlogPosts;
